import React, { Component } from "react";
import { message, Tag, Checkbox, Progress, Select } from "antd";
import axios from "axios";
import { connect } from "react-redux";
import { ModalItem, ButtonItem } from "../../../../Components/Items";
import {
  DownloadOutlined, PrinterOutlined,
  SortAscendingOutlined, SortDescendingOutlined,
} from "@ant-design/icons";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  OPCIONES_ORDEN, ORDEN_POR_DEFECTO, idsNoSuma, idsEspecie, ordenarLineas, sufijoOrden,
} from "../ordenLineas";
import { etiquetaFiniquito, sufijoFiniquito } from "../filtroFiniquito";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Visor de TODOS los recibos de nómina en un solo PDF, para mandarlos a imprimir
 * de una vez en lugar de abrir uno por uno los archivos del ZIP.
 *
 * ── POR QUÉ YA NO VA EN BLOQUES ─────────────────────────────────────────────
 * Antes esto pedía `prenomina/recibosNominaUnPdf`, que arma el PDF en el
 * servidor y por eso tiene un tope duro de 50 recibos: la selección se partía en
 * bloques y el usuario tenía que navegarlos a mano — y para bajar el documento
 * completo, visitarlos todos.
 *
 * Ahora usa `prenomina/recibosNominaPartesPdf`, que devuelve el árbol de
 * contenido en JSON y deja el render a pdfmake **en el navegador**. Del costo del
 * servidor sólo quedan las consultas, y ésas ya se cargan por lote
 * (`_datosRecibosLote`: ~15 consultas para toda la tanda, no 19 por recibo). Con
 * eso el troceo sigue existiendo, pero es invisible: se ve una barra de avance y
 * al final UN documento con todos.
 *
 * El documento se arma con `docBase` y `watermarkSinTimbrar` que manda el propio
 * servidor, no con copias aquí: la hoja de estilos y el tamaño de página siguen
 * viviendo sólo en `Pdf/reciboNominaPdf.js`.
 *
 * ── ORDEN DE IMPRESIÓN ──────────────────────────────────────────────────────
 * El backend respeta el orden del arreglo `lineaIds` que recibe, así que elegir
 * en qué orden salen los recibos es sólo elegir en qué orden se mandan los ids
 * (ver ordenLineas.js). Por eso aquí llegan las LÍNEAS completas y no los ids:
 * ordenar por código, departamento o registro patronal necesita los campos que
 * datosPrenomina inyecta en cada línea.
 *
 * Props:
 *   lineas           todas las líneas con recibo, ya filtradas por el padre
 *   conceptosNomina  catálogo vivo, para resolver `noSuma` en el orden por Neto
 *   nombrePrenomina  para el nombre del archivo al descargar
 *   filtroFiniquito  'sin' | 'solo' | 'todos' (ver filtroFiniquito.js). Las líneas ya
 *                    llegan filtradas; esto sólo pone el tag del título y el sufijo
 *                    del archivo, para que se vea qué se está imprimiendo.
 */

// Recibos por petición. Debe ir igual o por debajo de MAX_RECIBOS_PARTES_PDF del
// backend (Controllers/Prenomina/cierre.controllers.js). El usuario no ve estas
// tandas: sólo la barra de avance.
const TAM_TANDA = 250;

class ModalVerRecibos extends Component {
  state = {
    loading: false,
    // { hechos, total } mientras se piden las tandas.
    progreso: { hechos: 0, total: 0 },
    pdfUrl: null,
    // Deja fuera los recibos sin CFDI, para imprimir sólo definitivos.
    soloTimbrados: false,
    // Orden en que salen los recibos dentro del documento. No se recuerda entre
    // aperturas a propósito: cada impresión decide el suyo.
    orden: ORDEN_POR_DEFECTO,
    ordenDescendente: false,
  };

  // El documento ya armado, para poder descargarlo sin volver a construirlo.
  _pdf = null;
  // Marca de corrida: si el usuario cierra el modal o cambia el filtro a media
  // carga, lo que traiga la corrida vieja se descarta en vez de pisar el visor.
  _corrida = 0;

  componentDidUpdate(prevProps) {
    if (this.props.visible === prevProps.visible) return;
    if (this.props.visible) {
      this.setState(
        { soloTimbrados: false, orden: ORDEN_POR_DEFECTO, ordenDescendente: false },
        () => this.armarDocumento()
      );
    } else {
      this.limpiar();
    }
  }

  componentWillUnmount() {
    this.limpiar();
  }

  /**
   * La object URL hay que revocarla a mano o el blob se queda en memoria mientras
   * viva la pestaña.
   */
  limpiar() {
    this._corrida += 1;
    if (this.state.pdfUrl) URL.revokeObjectURL(this.state.pdfUrl);
    this._pdf = null;
    this.setState({ pdfUrl: null, loading: false, progreso: { hechos: 0, total: 0 } });
  }

  /**
   * Cualquier cambio de selección o de orden rearma el documento completo: el
   * contenido llega del servidor ya concatenado en el orden que se pidió, así
   * que no hay de dónde recortarlo ni reacomodarlo aquí. Es el mismo costo que
   * ya tenía el check de "solo timbrados", y ambas cosas se eligen una vez por
   * impresión.
   */
  reconstruirCon(cambios) {
    this.limpiar();
    this.setState(cambios, () => this.armarDocumento());
  }

  /**
   * Las líneas timbradas. Se derivan aquí y no las manda el padre porque este
   * modal ya recibe las líneas completas.
   */
  lineasTimbradas() {
    return (this.props.lineas || []).filter(l => l.estatus === "timbrada");
  }

  /**
   * Recibos que salieron en ceros y por eso no llevan CFDI. No son "pendientes de
   * timbrar": ya se decidió que no se timbran, así que se cuentan aparte para no
   * engordar el aviso naranja de los que sí están esperando su timbre.
   */
  lineasSinTimbre() {
    return (this.props.lineas || []).filter(l => l.estatus === "sin_timbre");
  }

  /**
   * Las líneas que entran al documento, sin ordenar. El render sólo necesita
   * cuántas son, y eso no depende del orden.
   */
  lineasActivas() {
    return this.state.soloTimbrados
      ? this.lineasTimbradas()
      : (this.props.lineas || []);
  }

  /**
   * Los ids que se están viendo, EN EL ORDEN EN QUE SE VAN A IMPRIMIR. Las
   * líneas vienen ya filtradas por quien abre el modal (respeta el buscador de
   * la tabla); el check de aquí las recorta a las que tienen CFDI y el selector
   * de orden decide la secuencia.
   */
  idsActivos() {
    return ordenarLineas(
      this.lineasActivas(),
      this.state.orden,
      this.state.ordenDescendente,
      idsNoSuma(this.props.conceptosNomina),
      // Sin esto, "ordenar por Neto" ordenaría por un neto que incluye lo entregado en
      // especie y no cuadraría con la columna Neto de la tabla.
      idsEspecie(this.props.conceptosNomina)
    ).map(l => l._id);
  }

  async armarDocumento() {
    const lineaIds = this.idsActivos();
    if (lineaIds.length === 0) return;

    const corrida = ++this._corrida;
    this.setState({ loading: true, progreso: { hechos: 0, total: lineaIds.length } });

    const contenido = [];
    let logo = null;
    let docBase = null;
    let watermark = null;
    let todosSinTimbrar = true;

    try {
      for (let i = 0; i < lineaIds.length; i += TAM_TANDA) {
        const tanda = lineaIds.slice(i, i + TAM_TANDA);
        const res = await axios.post(
          "prenomina/recibosNominaPartesPdf",
          {
            empresaId: this.props.empresaId,
            lineaIds: tanda,
            // Sin esto, el primer recibo de la segunda tanda se pegaría al último
            // de la primera en vez de empezar en página nueva.
            continuacion: contenido.length > 0,
          },
          { headers: this.props.headersToken }
        );

        // El usuario cerró el modal o cambió el filtro mientras esto viajaba.
        if (corrida !== this._corrida) return;
        if (res.data.success !== 2) continue;

        contenido.push(...res.data.contenido);
        logo = logo || res.data.logo;
        docBase = docBase || res.data.docBase;
        watermark = watermark || res.data.watermarkSinTimbrar;
        todosSinTimbrar = todosSinTimbrar && res.data.todosSinTimbrar;

        this.setState({
          progreso: { hechos: Math.min(i + tanda.length, lineaIds.length), total: lineaIds.length },
        });
      }
    } catch {
      if (corrida !== this._corrida) return;
      message.error("Error al generar los recibos");
      this.setState({ loading: false });
      return;
    }

    if (corrida !== this._corrida) return;
    if (contenido.length === 0 || !docBase) {
      message.warning("No se pudo generar ningún recibo");
      this.setState({ loading: false });
      return;
    }

    // El watermark del documento sólo va si TODA la selección está sin timbrar;
    // los recibos sueltos sin timbrar ya traen su propia banda en el contenido.
    this._pdf = pdfMake.createPdf({
      ...(todosSinTimbrar && watermark ? { watermark } : {}),
      ...docBase,
      content: contenido,
      images: logo ? { logo } : {},
    });

    this._pdf.getBlob((blob) => {
      // getBlob es asíncrono: puede volver después de que el usuario cerró.
      if (corrida !== this._corrida) return;
      this.setState({ loading: false, pdfUrl: URL.createObjectURL(blob) });
    });
  }

  /**
   * Imprime desde el iframe, que es lo que abre el diálogo del navegador con
   * todas las páginas de una sola vez. Si el navegador lo bloquea (algunos no
   * dejan tocar el contentWindow de un blob), se avisa y que use el visor.
   */
  imprimir() {
    const marco = document.getElementById("nominaRecibosPdf");
    try {
      if (marco?.contentWindow) {
        marco.contentWindow.focus();
        marco.contentWindow.print();
        return;
      }
    } catch { /* cae al aviso */ }
    message.info("Si no se abrió el diálogo de impresión, usa el botón de imprimir del visor de PDF");
  }

  descargar() {
    if (!this._pdf) return;
    // El filtro y el orden van en el nombre: si no, dos descargas de la misma
    // nómina —una completa y una de solo timbrados, o la misma en otro orden—
    // salen con el mismo archivo.
    const finiquitos = sufijoFiniquito(this.props.filtroFiniquito);
    const filtro = this.state.soloTimbrados ? "_timbrados" : "";
    const orden = sufijoOrden(this.state.orden, this.state.ordenDescendente);
    const nombre = `Recibos_${this.props.nombrePrenomina || "nomina"}${finiquitos}${filtro}${orden}.pdf`;
    this._pdf.download(nombre.replace(/[/\\?*[\]:#]/g, "_"));
  }

  render() {
    const { visible, onClose, filtroFiniquito } = this.props;
    const { loading, pdfUrl, soloTimbrados, progreso, orden, ordenDescendente } = this.state;
    const etiquetaFiltro = etiquetaFiniquito(filtroFiniquito);

    // Se cuenta sobre las líneas sin ordenar: el orden no cambia cuántas son y
    // ordenar en cada render sería trabajo tirado.
    const totalRecibos = this.lineasActivas().length;
    const totalTimbrados = this.lineasTimbradas().length;
    const totalSinTimbre = this.lineasSinTimbre().length;
    // Los que de verdad están esperando timbre: ni los timbrados ni los que salieron
    // en ceros, que nunca van a tener CFDI.
    const totalSinTimbrar =
      (this.props.lineas || []).length - totalTimbrados - totalSinTimbre;
    const porcentaje = progreso.total > 0
      ? Math.round((progreso.hechos / progreso.total) * 100)
      : 0;

    return (
      <ModalItem
        title={(
          <span>
            Recibos de Nómina
            <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
              {totalRecibos} recibo{totalRecibos === 1 ? "" : "s"}
            </Tag>
            {/* Morado = el color de los finiquitos en los chips de totales de TablaPrenomina. */}
            {etiquetaFiltro && (
              <Tag color={filtroFiniquito === "solo" ? "purple" : "default"} style={{ fontSize: 11 }}>
                {etiquetaFiltro}
              </Tag>
            )}
            {totalSinTimbrar > 0 && !soloTimbrados && (
              <Tag color="orange" style={{ fontSize: 11 }}>
                {totalSinTimbrar} sin timbrar
              </Tag>
            )}
            {totalSinTimbre > 0 && !soloTimbrados && (
              <Tag color="default" style={{ fontSize: 11 }}>
                {totalSinTimbre} en ceros
              </Tag>
            )}
            {soloTimbrados && (
              <Tag color="purple" style={{ fontSize: 11 }}>
                solo timbrados
              </Tag>
            )}
          </span>
        )}
        visible={visible}
        onCancel={onClose}
        paddingTop
        width={950}
      >
        {(totalSinTimbrar > 0 || totalSinTimbre > 0) && (
          <div style={{
            background: soloTimbrados ? "#F6FFED" : "#FFF7E6",
            border: `1px solid ${soloTimbrados ? "#B7EB8F" : "#FFD591"}`,
            borderRadius: 6, padding: "8px 12px", marginBottom: 12,
            fontSize: 12, color: soloTimbrados ? "#237804" : "#874D00",
          }}>
            {soloTimbrados ? (
              <>
                Se están mostrando sólo los {totalRecibos} recibo
                {totalRecibos === 1 ? "" : "s"} con CFDI. Los {totalSinTimbrar + totalSinTimbre} sin
                timbrar quedaron fuera, así que lo que se imprima son definitivos.
              </>
            ) : (
              <>
                {totalSinTimbrar > 0 && (
                  <>
                    Hay {totalSinTimbrar} recibo{totalSinTimbrar === 1 ? "" : "s"} sin timbrar en
                    la selección. Cada uno sale marcado como vista previa en su propia página;
                    los que ya están timbrados salen limpios.
                  </>
                )}
                {totalSinTimbre > 0 && (
                  <div style={{ marginTop: totalSinTimbrar > 0 ? 6 : 0 }}>
                    {totalSinTimbre} recibo{totalSinTimbre === 1 ? "" : "s"} salió en ceros y no
                    lleva{totalSinTimbre === 1 ? "" : "n"} CFDI: sin percepciones el SAT rechaza
                    el comprobante, así que no se timbra{totalSinTimbre === 1 ? "" : "n"}.
                    También sale{totalSinTimbre === 1 ? "" : "n"} marcado
                    {totalSinTimbre === 1 ? "" : "s"} como vista previa.
                  </div>
                )}
              </>
            )}
            <div style={{ marginTop: 6 }}>
              <Checkbox
                checked={soloTimbrados}
                // Sin ninguno timbrado el check dejaría el visor en blanco.
                disabled={totalTimbrados === 0 || loading}
                onChange={(e) => this.reconstruirCon({ soloTimbrados: e.target.checked })}
              >
                Solo los que ya están timbrados
              </Checkbox>
              {totalTimbrados === 0 && (
                <span style={{ marginLeft: 8, color: "#888" }}>
                  (ninguno de esta selección está timbrado todavía)
                </span>
              )}
            </div>
          </div>
        )}

        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          marginBottom: 12, flexWrap: "wrap",
        }}>
          {/* El orden en que se imprimen. Cambiarlo rearma el documento, igual
              que el check de arriba: el servidor manda el contenido ya
              concatenado en el orden que se le pidió. */}
          <span style={{ fontSize: 12, color: "#888" }}>Ordenar por</span>
          <Select
            size="small"
            style={{ width: 190 }}
            value={orden}
            disabled={loading}
            onChange={(valor) => this.reconstruirCon({ orden: valor })}
          >
            {OPCIONES_ORDEN.map((o) => (
              <Select.Option value={o.valor} key={o.valor}>{o.etiqueta}</Select.Option>
            ))}
          </Select>
          <ButtonItem
            blanco size="small"
            icon={ordenDescendente ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
            disabled={loading}
            title={ordenDescendente ? "Orden descendente" : "Orden ascendente"}
            onClick={() => this.reconstruirCon({ ordenDescendente: !ordenDescendente })}
          />
          <span style={{ flex: 1 }} />
          <ButtonItem
            morado size="small" icon={<PrinterOutlined />}
            disabled={!pdfUrl} onClick={this.imprimir.bind(this)}
          >
            Imprimir todos
          </ButtonItem>
          <ButtonItem
            blanco size="small" icon={<DownloadOutlined />}
            disabled={!pdfUrl} onClick={this.descargar.bind(this)}
          >
            Descargar
          </ButtonItem>
          <ButtonItem type="primary" danger size="small" onClick={onClose}>
            Cerrar
          </ButtonItem>
        </div>

        {/* Mientras se arma no hay iframe que mostrar: en su lugar va el avance,
            que con nóminas grandes son varios segundos y sin esto se ve colgado. */}
        {loading ? (
          <div style={{
            height: "72vh", border: "1px solid #eee", borderRadius: 4,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 12,
          }}>
            <div style={{ width: "60%" }}>
              <Progress percent={porcentaje} status="active" showInfo={progreso.total > 0} />
            </div>
            <div style={{ fontSize: 13, color: "#888" }}>
              {progreso.total > 0
                ? `Preparando ${progreso.hechos} de ${progreso.total} recibos…`
                : "Preparando los recibos…"}
            </div>
            {progreso.hechos >= progreso.total && progreso.total > 0 && (
              <div style={{ fontSize: 12, color: "#aaa" }}>Armando el documento…</div>
            )}
          </div>
        ) : (
          <iframe
            title="recibos-nomina"
            style={{ width: "100%", height: "72vh", border: "1px solid #eee" }}
            id="nominaRecibosPdf"
            name="nominaRecibosPdf"
            src={pdfUrl || undefined}
          />
        )}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalVerRecibos);
